import { useTranslation } from 'react-i18next';

import type { ListingStage } from '@/api/v4/project-listings.api';
import { colors } from '@/theme/colors';
import type { DropdownOption } from '@/ui/dropdown/dropdown';
import { WithDropdown } from '@/ui/dropdown/dropdown';

import type { StagePillKey } from '../stage-pills/spotlight-company-stage-pills';
import {
  ControlButton,
  ControlsRow,
  ControlsSection,
  FilterButtonContainer,
  SearchControl,
  SearchInput,
  ShowingCompanies,
  SortingOptionEmoji,
  SortingOptionLabel,
} from './spotlight-companies-listing.styles';
import { SpotlightStageFiltersPanel } from './spotlight-stage-filters-panel';
import type { SpotlightCompanySortMode } from './use-spotlight-companies.query';

interface SpotlightCompaniesControlsProps {
  displayedCompaniesCount: number;
  displayedTotalItems: number;
  handleClearStageFilters: () => void;
  handleCloseStageFiltersPanel: () => void;
  handleRemoveSearch: () => void;
  handleSearchPhraseChange: (value: string) => void;
  handleToggleStageFilter: (stageFilter: StagePillKey) => void;
  handleToggleStageFiltersPanel: () => void;
  hasActiveStageFilters: boolean;
  isFilterControlActive: boolean;
  isSortControlActive: boolean;
  isStageFilterOpen: boolean;
  listingStages: ListingStage[];
  searchValue: string;
  selectedStageFilters: StagePillKey[];
  setIsSortDropdownOpen: (isOpen: boolean) => void;
  setSortMode: (sortMode: SpotlightCompanySortMode) => void;
  sortMode: SpotlightCompanySortMode;
}

export const SpotlightCompaniesControls = ({
  displayedCompaniesCount,
  displayedTotalItems,
  handleClearStageFilters,
  handleCloseStageFiltersPanel,
  handleRemoveSearch,
  handleSearchPhraseChange,
  handleToggleStageFilter,
  handleToggleStageFiltersPanel,
  hasActiveStageFilters,
  isFilterControlActive,
  isSortControlActive,
  isStageFilterOpen,
  listingStages,
  searchValue,
  selectedStageFilters,
  setIsSortDropdownOpen,
  setSortMode,
  sortMode,
}: SpotlightCompaniesControlsProps) => {
  const { t } = useTranslation(['companies', 'spotlight']);

  const sortingItems: DropdownOption[] = [
    {
      icon: sortMode === 'alphabetical' ? 'Check' : undefined,
      label: (
        <SortingOptionLabel>
          <SortingOptionEmoji aria-hidden="true">🔤</SortingOptionEmoji>
          <span>{t('spotlight:alphabetical')}</span>
        </SortingOptionLabel>
      ),
      onClick: () => setSortMode('alphabetical'),
    },
    {
      icon: sortMode === 'lastAdded' ? 'Check' : undefined,
      label: (
        <SortingOptionLabel>
          <SortingOptionEmoji aria-hidden="true">📅</SortingOptionEmoji>
          <span>{t('spotlight:lastAdded')}</span>
        </SortingOptionLabel>
      ),
      onClick: () => setSortMode('lastAdded'),
    },
  ];

  const sortingLabel =
    sortMode === 'lastAdded'
      ? t('spotlight:sortingLastAdded')
      : t('spotlight:sortingAlphabetical');

  return (
    <ControlsSection>
      <ControlsRow>
        <SearchControl>
          <SearchInput
            onChange={handleSearchPhraseChange}
            placeholder={t('spotlight:searchCompanies')}
            onRemove={handleRemoveSearch}
            disabled={false}
            isInputActivated={true}
            onInputActivate={() => {}}
            onInputDeactivate={() => {}}
            defaultValue={searchValue}
            controlHeight="42px"
            inputWidth="100%"
            searchInputIcon="SpotlightSearch"
            searchInputIconSize="20px"
            searchInputIconColor={colors.spotlight.text.secondary}
            displayIconOnly
            delay={400}
          />
        </SearchControl>
        <WithDropdown
          items={sortingItems}
          setIsDropdownOpened={setIsSortDropdownOpen}
        >
          <ControlButton
            variant="white"
            height={42}
            contentPadding="lg"
            startIcon="SpotlightSortArrowUpDown"
            iconColor={
              isSortControlActive
                ? colors.basics.white
                : colors.spotlight.text.primary
            }
            data-is-active={isSortControlActive}
          >
            {sortingLabel}
          </ControlButton>
        </WithDropdown>
        <FilterButtonContainer data-stage-filters-toggle="true">
          <ControlButton
            variant="white"
            height={42}
            contentPadding="lg"
            startIcon="SpotlightListFilter"
            iconColor={
              isFilterControlActive
                ? colors.basics.white
                : colors.spotlight.text.primary
            }
            onClick={handleToggleStageFiltersPanel}
            data-is-active={isFilterControlActive}
          >
            {selectedStageFilters.length > 0
              ? t('spotlight:filterCount', {
                  count: selectedStageFilters.length,
                })
              : t('spotlight:filter')}
          </ControlButton>
        </FilterButtonContainer>
        <SpotlightStageFiltersPanel
          hasActiveStageFilters={hasActiveStageFilters}
          isOpen={isStageFilterOpen}
          listingStages={listingStages}
          onClearStageFilters={handleClearStageFilters}
          onClose={handleCloseStageFiltersPanel}
          onToggleStageFilter={handleToggleStageFilter}
          selectedStageFilters={selectedStageFilters}
        />
      </ControlsRow>
      <ShowingCompanies>
        {t('spotlight:showingCompanies', {
          count: displayedCompaniesCount,
          total: displayedTotalItems,
        })}
      </ShowingCompanies>
    </ControlsSection>
  );
};
